import { computed, type ComputedRef, type UnwrapRef } from "vue";
import { DateTime } from "luxon";
import notEmpty from "@/functions/notEmpty";
import { type ActionListActionFields_Task_Fragment, IdeaRecommend, IdeaVeto } from "@/graphql";
import { LISA_DATE_SHORT, t } from "@/language/translation";

export type Comments = NonNullable<UnwrapRef<ReturnType<typeof useRevisions>>>;

export function formatRevisions(
  revisions?: (Pick<
    NonNullable<ActionListActionFields_Task_Fragment["Revisions"]>[number],
    "operation" | "id" | "createdAt"
  > & {
    TaskRevisions?: Pick<
      NonNullable<
        NonNullable<ActionListActionFields_Task_Fragment["Revisions"]>[number]["TaskRevisions"]
      >[number],
      "comment"
    >[];
    RevisionChanges?: (Pick<
      NonNullable<
        NonNullable<ActionListActionFields_Task_Fragment["Revisions"]>[number]["RevisionChanges"]
      >[number],
      "path"
    > & {
      document?: {
        path?: string[];
      };
      oldUser?: {
        preferredName?: string;
      };
      newUser?: {
        preferredName?: string;
      };
      changes?: {
        previousValue?: any;
        newValue?: any;
      };
    })[];
    assignee?: {
      firstname: string;
      lastname: string;
    };
  })[],
  long = false,
) {
  return (revisions ?? [])
    .filter((revision) => long || revision.operation !== "create")
    .map((revision) => {
      if (!revision.TaskRevisions || !revision.RevisionChanges) {
        return null;
      }
      const userComments = revision.TaskRevisions.map((taskRevision) => taskRevision.comment);

      const changes = revision.RevisionChanges.map((curr) => {
        if (!curr || !curr.changes) {
          return null;
        }
        const field = curr.path;

        if (field === "status") {
          return {
            field,
            from: t(`components.TaskModal.columns.status${curr.changes.previousValue || 1}`),
            to: t(`components.TaskModal.columns.status${curr.changes.newValue || 1}`),
          };
        }

        if (field === "deadline") {
          return {
            field,
            from:
              curr.changes.previousValue != null
                ? DateTime.fromISO(curr.changes.previousValue).toLocaleString(LISA_DATE_SHORT)
                : "",
            to:
              curr.changes.newValue != null
                ? DateTime.fromISO(curr.changes.newValue).toLocaleString(LISA_DATE_SHORT)
                : "",
          };
        }

        if (field === "assigneeId") {
          return {
            field: "Assignee",
            from: curr.oldUser?.preferredName ?? t("global.deletedUser"),
            to: curr.newUser?.preferredName ?? t("global.deletedUser"),
          };
        }

        if (
          field === "meta" &&
          curr.changes.newValue != null &&
          curr.document?.path?.length === 4 &&
          curr.document.path[1] === "customAttributes" &&
          curr.document.path[3] === "value"
        ) {
          return {
            field: curr.document.path[2],
            noTranslate: true,
            from: curr.changes.previousValue,
            to: curr.changes.newValue,
          };
        }
        if (
          field === "meta" &&
          curr.changes.newValue != null &&
          [IdeaVeto.noVeto, IdeaVeto.veto].includes(curr.changes.newValue)
        ) {
          return {
            field: "veto",
            from: t(`components.TaskModal.idea.${curr.changes.previousValue}`),
            to: t(`components.TaskModal.idea.${curr.changes.newValue}`),
          };
        }
        if (
          field === "meta" &&
          curr.changes.newValue != null &&
          [
            IdeaRecommend.recommend,
            IdeaRecommend.recommendNot,
            IdeaRecommend.recommendNotPossible,
          ].includes(curr.changes.newValue)
        ) {
          return {
            field: "recommend",
            from: t(`components.TaskModal.idea.${curr.changes.previousValue}`),
            to: t(`components.TaskModal.idea.${curr.changes.newValue}`),
          };
        }
        if (long && field === "description") {
          // Removes empty descriptions for ideaTasks & manageTasks
          if (!curr.changes.previousValue && !curr.changes.newValue) {
            return null;
          }
          return {
            field,
            from: curr.changes.previousValue,
            to: curr.changes.newValue,
          };
        }
        return null;
      }).filter(notEmpty);

      if (changes.length === 0 && userComments.length === 0) {
        return null;
      }

      if (revision.operation === "create") {
        changes.forEach((change) => {
          change.from = undefined;
        });
      }

      const shortenedName = revision.assignee
        ? `${revision.assignee?.firstname[0]}. ${revision.assignee?.lastname}`
        : t("global.deletedUser");

      return {
        id: revision.id,
        user: shortenedName,
        date: DateTime.fromISO(revision.createdAt),
        userComments,
        changes,
      };
    })
    .filter(notEmpty);
}

export function useRevisions(
  revisions: ComputedRef<ActionListActionFields_Task_Fragment["Revisions"]>,
  long = false,
) {
  return computed(() => {
    return formatRevisions(revisions.value, long);
  });
}
